// Unified terminology data - single source of truth for both auto-mapping and UI reference

export interface TermEntry {
  field: string;           // Internal field name used in COLUMN_SYNONYMS
  standardName: string;    // Display name for UI
  description: string;
  alternativeNames: string[];
  unit?: string;
  example?: string;
}

export interface CategoryTerms {
  category: string;
  color: string;
  terms: TermEntry[];
}

// Complete terminology data with synonyms, descriptions, units, and examples
export const TERMINOLOGY_DATA: CategoryTerms[] = [
  {
    category: "GPS & Training Load",
    color: "text-foreground",
    terms: [
      {
        field: "total_distance_m",
        standardName: "Total Distance",
        description: "Total distance covered during a session or match",
        alternativeNames: ["total distance", "distance", "totaldist", "total dist", "tot dist", "distance (m)", "total distance (m)", "td", "distance covered"],
        unit: "meters (m) or kilometers (km)",
        example: "8,500m"
      },
      {
        field: "hsd_distance_m",
        standardName: "High-Speed Distance (HSD)",
        description: "Distance covered above a high-speed threshold (typically >19.8 km/h or >5.5 m/s)",
        alternativeNames: ["hsd", "high speed", "high-speed distance", "hsr", "high speed running", "hsr distance (m)", "high speed running (m)", "zone 5 distance", "hi distance"],
        unit: "meters (m)",
        example: "850m"
      },
      {
        field: "sprint_distance_m",
        standardName: "Sprint Distance",
        description: "Distance covered at sprint speeds (typically >25.2 km/h or >7 m/s)",
        alternativeNames: ["sprint", "sprint distance", "sprinting", "vhsr", "very high speed", "sprint distance (m)", "zone 6", "max speed distance", "vhs distance"],
        unit: "meters (m)",
        example: "120m"
      },
      {
        field: "player_load",
        standardName: "Total Load",
        description: "Accumulated accelerometer-based load metric derived from tri-axial accelerometer data",
        alternativeNames: ["load", "pl", "training load", "playerload", "body load", "accumulated load", "player load", "dynamic stress load", "accel load"],
        unit: "arbitrary units (AU)",
        example: "450 AU"
      },
      {
        field: "max_velocity_ms",
        standardName: "Max Velocity",
        description: "Peak speed reached during the session",
        alternativeNames: ["max speed", "max velocity", "top speed", "peak speed", "vmax", "max speed (m/s)", "maximum velocity"],
        unit: "m/s or km/h",
        example: "9.2 m/s (33.1 km/h)"
      },
      {
        field: "accel_count",
        standardName: "Accelerations",
        description: "Count of high-intensity accelerations (typically >3 m/s²)",
        alternativeNames: ["accelerations", "accel", "acc", "accel count", "accelerations >3 m/s²", "accelerations >3", "high accels", "acceleration events", "accel efforts"],
        unit: "count",
        example: "45"
      },
      {
        field: "decel_count",
        standardName: "Decelerations",
        description: "Count of high-intensity decelerations (typically <-3 m/s²)",
        alternativeNames: ["decelerations", "decel", "dec", "decel count", "decelerations >3 m/s²", "decelerations >3", "high decels", "deceleration events", "braking events"],
        unit: "count",
        example: "42"
      },
      {
        field: "sprint_count",
        standardName: "Sprint Count",
        description: "Number of sprints performed during the session",
        alternativeNames: ["sprint count", "sprints", "number of sprints"],
        unit: "count",
        example: "12"
      },
      {
        field: "hmld_distance_m",
        standardName: "HMLD",
        description: "High Metabolic Load Distance - distance covered at high metabolic power",
        alternativeNames: ["hmld", "high metabolic load distance", "hmld distance"],
        unit: "meters (m)",
        example: "1,200m"
      },
      {
        field: "rpe",
        standardName: "sRPE",
        description: "Session Rating of Perceived Exertion (subjective intensity × duration)",
        alternativeNames: ["rating", "perceived exertion", "effort", "intensity", "srpe", "session rpe", "training load", "rpe load", "subjective load", "internal load"],
        unit: "arbitrary units",
        example: "540 (RPE 6 × 90 min)"
      }
    ]
  },
  {
    category: "Wellness & Recovery",
    color: "text-foreground",
    terms: [
      {
        field: "sleep_quality",
        standardName: "Sleep Quality",
        description: "Subjective rating of sleep quality",
        alternativeNames: ["sleep", "sleep quality", "rest", "sleep score", "sleep rating", "rest quality", "how well slept"],
        unit: "1-10 or 1-5 scale",
        example: "8/10"
      },
      {
        field: "fatigue",
        standardName: "Fatigue",
        description: "Subjective feeling of tiredness/energy levels",
        alternativeNames: ["tiredness", "tired", "fatigue level", "energy", "energy level", "how fresh"],
        unit: "1-10 or 1-5 scale",
        example: "3/10 (low fatigue = good)"
      },
      {
        field: "soreness",
        standardName: "Soreness",
        description: "Muscle soreness or general body soreness",
        alternativeNames: ["muscle soreness", "doms", "pain", "body soreness", "stiffness", "how sore"],
        unit: "1-10 or 1-5 scale",
        example: "4/10"
      },
      {
        field: "stress",
        standardName: "Stress",
        description: "Mental/emotional stress levels",
        alternativeNames: ["stress level", "pressure", "mental stress", "anxiety", "how stressed"],
        unit: "1-10 or 1-5 scale",
        example: "2/10"
      },
      {
        field: "mood",
        standardName: "Mood",
        description: "General mood or mental state",
        alternativeNames: ["mood state", "feeling", "wellbeing", "mental state", "happiness", "how feeling"],
        unit: "1-10 or 1-5 scale",
        example: "8/10"
      }
    ]
  },
  {
    category: "Metabolic Testing",
    color: "text-foreground",
    terms: [
      {
        field: "vo2max",
        standardName: "VO2max",
        description: "Maximum oxygen uptake - gold standard measure of aerobic fitness",
        alternativeNames: ["max vo2", "aerobic capacity", "maximal oxygen consumption", "vo2 peak"],
        unit: "ml/kg/min",
        example: "55.2 ml/kg/min"
      },
      {
        field: "mas",
        standardName: "MAS",
        description: "Maximal Aerobic Speed - velocity at VO2max",
        alternativeNames: ["vvo2max", "maximum aerobic speed", "vam", "velocity at vo2max"],
        unit: "km/h or m/s",
        example: "17.5 km/h"
      },
      {
        field: "vt1",
        standardName: "VT1 (Ventilatory Threshold 1)",
        description: "First lactate/ventilatory threshold - aerobic threshold",
        alternativeNames: ["lt1", "aerobic threshold", "first threshold", "vt", "lactate threshold 1"],
        unit: "km/h, %VO2max, or bpm",
        example: "11.5 km/h"
      },
      {
        field: "vt2",
        standardName: "VT2 (Ventilatory Threshold 2)",
        description: "Second lactate/ventilatory threshold - anaerobic threshold",
        alternativeNames: ["lt2", "anaerobic threshold", "mlss", "obla", "lactate threshold 2", "critical speed"],
        unit: "km/h, %VO2max, or bpm",
        example: "14.5 km/h"
      },
      {
        field: "hr_max",
        standardName: "HR Max",
        description: "Maximum heart rate achieved",
        alternativeNames: ["max hr", "maximum heart rate", "peak hr", "hrmax"],
        unit: "bpm",
        example: "192 bpm"
      },
      {
        field: "hr_recovery",
        standardName: "HR Recovery",
        description: "Heart rate drop in the first minute after exercise",
        alternativeNames: ["hrr", "recovery hr", "hr drop", "1-min recovery", "hrr1"],
        unit: "bpm (drop)",
        example: "45 bpm"
      },
      {
        field: "running_economy",
        standardName: "Running Economy",
        description: "Oxygen cost of running at a given speed",
        alternativeNames: ["re", "economy", "o2 cost", "efficiency", "energy cost"],
        unit: "ml/kg/km",
        example: "210 ml/kg/km"
      }
    ]
  },
  {
    category: "Strength & Power",
    color: "text-foreground",
    terms: [
      {
        field: "cmj_height",
        standardName: "CMJ Height",
        description: "Counter-movement jump height",
        alternativeNames: ["jump height", "vertical jump", "cmj", "counter movement jump", "vj"],
        unit: "cm or inches",
        example: "42cm"
      },
      {
        field: "peak_force",
        standardName: "Peak Force",
        description: "Maximum force produced during a movement",
        alternativeNames: ["max force", "fmax", "peak ground reaction force", "pf"],
        unit: "Newtons (N) or N/kg",
        example: "2,450N"
      },
      {
        field: "rfd",
        standardName: "Rate of Force Development (RFD)",
        description: "How quickly force is produced",
        alternativeNames: ["rfd", "explosive strength", "force development rate", "rate of torque development"],
        unit: "N/s or N/ms",
        example: "8,500 N/s"
      },
      {
        field: "relative_peak_power",
        standardName: "Relative Peak Power",
        description: "Peak power output relative to body mass",
        alternativeNames: ["peak power", "power output", "relative power", "w/kg", "power/bw"],
        unit: "W/kg",
        example: "52.3 W/kg"
      },
      {
        field: "asymmetry_percent",
        standardName: "Asymmetry",
        description: "Difference between left and right limb",
        alternativeNames: ["asymmetry", "imbalance", "difference", "asymmetry %", "bilateral asymmetry", "asymmetry (%)", "l/r difference", "side difference", "limb symmetry index"],
        unit: "percentage (%)",
        example: "8.5%"
      },
      {
        field: "imtp",
        standardName: "Isometric Mid-Thigh Pull (IMTP)",
        description: "Maximal isometric force test",
        alternativeNames: ["imtp", "mid-thigh pull", "iso pull", "isometric strength"],
        unit: "Newtons (N) or N/kg",
        example: "3,200N"
      },
      {
        field: "one_rm",
        standardName: "1RM",
        description: "One repetition maximum - max weight for one rep",
        alternativeNames: ["one rep max", "max lift", "1-rep max", "max strength", "1rm"],
        unit: "kg or lbs",
        example: "120kg"
      },
      {
        field: "left_value",
        standardName: "Left Value",
        description: "Measurement for left limb/side",
        alternativeNames: ["left", "left side", "left leg", "left limb", "l", "left score", "left result"],
        unit: "varies",
        example: "42cm"
      },
      {
        field: "right_value",
        standardName: "Right Value",
        description: "Measurement for right limb/side",
        alternativeNames: ["right", "right side", "right leg", "right limb", "r", "right score", "right result"],
        unit: "varies",
        example: "45cm"
      }
    ]
  },
  {
    category: "Speed & Agility",
    color: "text-foreground",
    terms: [
      {
        field: "sprint_time",
        standardName: "Sprint Time",
        description: "Time to complete a sprint distance",
        alternativeNames: ["time", "split time", "sprint split", "run time"],
        unit: "seconds (s)",
        example: "3.02s (20m)"
      },
      {
        field: "ten_m_split",
        standardName: "10m Split",
        description: "Time to complete first 10 meters",
        alternativeNames: ["0-10m", "10m time", "first 10", "acceleration phase"],
        unit: "seconds (s)",
        example: "1.72s"
      },
      {
        field: "flying_10m",
        standardName: "Flying 10m",
        description: "Time for 10m at maximum velocity (after acceleration)",
        alternativeNames: ["f10", "max velocity 10m", "flying sprint", "10-20m"],
        unit: "seconds (s)",
        example: "1.08s"
      },
      {
        field: "acceleration",
        standardName: "Acceleration",
        description: "Rate of velocity increase",
        alternativeNames: ["accel", "0-10m speed", "explosive speed", "starting speed"],
        unit: "m/s²",
        example: "4.2 m/s²"
      },
      {
        field: "agility_time",
        standardName: "Agility Time",
        description: "Time to complete an agility test (5-0-5, T-test, etc.)",
        alternativeNames: ["cod time", "change of direction", "agility score", "pro agility"],
        unit: "seconds (s)",
        example: "2.35s (5-0-5)"
      },
      {
        field: "reactive_agility",
        standardName: "Reactive Agility",
        description: "Agility test with decision-making component",
        alternativeNames: ["rat", "reactive cod", "decision agility", "perceptual-motor agility"],
        unit: "seconds (s)",
        example: "1.85s"
      }
    ]
  },
  {
    category: "Cognitive Assessment",
    color: "text-foreground",
    terms: [
      {
        field: "reaction_time_ms",
        standardName: "Reaction Time",
        description: "Time to respond to a stimulus",
        alternativeNames: ["reaction time", "rt", "reaction", "response time", "simple rt", "choice rt"],
        unit: "milliseconds (ms)",
        example: "245ms"
      },
      {
        field: "visual_search_ms",
        standardName: "Visual Search",
        description: "Time to find target among distractors",
        alternativeNames: ["visual search", "vs", "search time", "visual processing", "attention task", "target detection"],
        unit: "milliseconds (ms)",
        example: "820ms"
      },
      {
        field: "go_no_go_accuracy_pct",
        standardName: "Go/No-Go Accuracy",
        description: "Accuracy on inhibition/response task",
        alternativeNames: ["go no go", "accuracy", "gonogo", "inhibition accuracy", "response accuracy", "gng accuracy", "impulse control"],
        unit: "percentage (%)",
        example: "94%"
      },
      {
        field: "working_memory",
        standardName: "Working Memory",
        description: "Score on working memory task",
        alternativeNames: ["n-back score", "memory span", "cognitive load", "short-term memory"],
        unit: "score or span",
        example: "6.5 span"
      },
      {
        field: "decision_time",
        standardName: "Decision Time",
        description: "Time to make a decision in a task",
        alternativeNames: ["dt", "choice time", "processing time", "cognitive speed"],
        unit: "milliseconds (ms)",
        example: "380ms"
      }
    ]
  },
  {
    category: "Body Composition",
    color: "text-foreground",
    terms: [
      {
        field: "weight_kg",
        standardName: "Body Mass",
        description: "Total body weight",
        alternativeNames: ["weight", "wt", "weight (kg)", "weight kg", "mass", "bw", "body weight", "total mass"],
        unit: "kg or lbs",
        example: "78.5kg"
      },
      {
        field: "body_fat_pct",
        standardName: "Body Fat Percentage",
        description: "Percentage of body mass that is fat",
        alternativeNames: ["bf%", "fat %", "body fat", "fat mass %", "adiposity"],
        unit: "percentage (%)",
        example: "12.5%"
      },
      {
        field: "lean_body_mass",
        standardName: "Lean Body Mass",
        description: "Total mass minus fat mass",
        alternativeNames: ["lbm", "fat-free mass", "lean mass", "ffm", "muscle mass"],
        unit: "kg or lbs",
        example: "68.7kg"
      },
      {
        field: "skeletal_muscle_mass",
        standardName: "Skeletal Muscle Mass",
        description: "Mass of skeletal muscle tissue",
        alternativeNames: ["smm", "muscle mass", "skeletal muscle", "appendicular lean mass"],
        unit: "kg",
        example: "35.2kg"
      },
      {
        field: "bmi",
        standardName: "BMI",
        description: "Body Mass Index (weight/height²)",
        alternativeNames: ["body mass index", "quetelet index"],
        unit: "kg/m²",
        example: "23.5 kg/m²"
      },
      {
        field: "height_cm",
        standardName: "Height",
        description: "Standing height",
        alternativeNames: ["height", "ht", "height (cm)", "height cm", "stature"],
        unit: "cm or inches",
        example: "182cm"
      },
      {
        field: "sum_of_skinfolds",
        standardName: "Sum of Skinfolds",
        description: "Combined thickness of skin folds at multiple sites",
        alternativeNames: ["skinfolds", "sum of 7", "sum of 8", "sf sum", "caliper measurement"],
        unit: "mm",
        example: "48mm (7-site)"
      },
      {
        field: "visceral_fat",
        standardName: "Visceral Fat Level",
        description: "Bioimpedance-derived rating of visceral adipose tissue. Requires InBody, Tanita, or similar device. Not a mass or percentage.",
        alternativeNames: ["visceral fat", "vfl", "visceral fat rating", "visceral level", "vat level"],
        unit: "level (1-59 scale)",
        example: "8 (healthy: 1-12, elevated: 13+)"
      }
    ]
  },
  {
    category: "Player Identification",
    color: "text-foreground",
    terms: [
      {
        field: "player_id",
        standardName: "Player ID",
        description: "Unique identifier or name of the athlete",
        alternativeNames: ["id", "player id", "playerid", "player", "athlete id", "athlete", "player name", "playername", "name", "full name", "athlete name", "squad no", "squad number", "#"],
        example: "John Smith or P001"
      },
      {
        field: "first_name",
        standardName: "First Name",
        description: "Given name",
        alternativeNames: ["first", "firstname", "given name", "forename", "first name"],
        example: "John"
      },
      {
        field: "last_name",
        standardName: "Last Name",
        description: "Family name",
        alternativeNames: ["last", "lastname", "surname", "family name", "last name"],
        example: "Smith"
      },
      {
        field: "external_ref",
        standardName: "External ID",
        description: "ID from external system (GPS, wellness app, etc.)",
        alternativeNames: ["external ref", "external id", "reference", "ref", "external reference", "player code", "system id", "device id", "tag id"],
        example: "P001, ATH-123"
      },
      {
        field: "jersey_number",
        standardName: "Jersey Number",
        description: "Shirt/kit number",
        alternativeNames: ["number", "no", "jersey", "#", "shirt number", "squad no", "squad number", "squad #", "kit number"],
        example: "10"
      }
    ]
  },
  {
    category: "Testing & Sessions",
    color: "text-foreground",
    terms: [
      {
        field: "test_date",
        standardName: "Test Date",
        description: "Date the test was performed",
        alternativeNames: ["date", "test date", "testing date", "assessment date", "date of test"],
        example: "2024-01-15"
      },
      {
        field: "session_date",
        standardName: "Session Date",
        description: "Date of the training session or match",
        alternativeNames: ["date", "session date", "training date", "match date", "start time"],
        example: "2024-01-15"
      },
      {
        field: "test_category",
        standardName: "Test Category",
        description: "Type/domain of the test",
        alternativeNames: ["category", "test category", "type", "test type", "testing category", "domain", "performance domain"],
        example: "Strength, Speed, Power"
      },
      {
        field: "test_name",
        standardName: "Test Name",
        description: "Specific name of the test",
        alternativeNames: ["test", "test name", "exercise", "assessment", "testing name", "test type", "specific test"],
        example: "CMJ, IMTP, 20m Sprint"
      },
      {
        field: "value",
        standardName: "Value",
        description: "Numeric result of the test",
        alternativeNames: ["result", "score", "measurement", "metric value", "performance", "test result", "numeric result"],
        example: "42.5"
      },
      {
        field: "unit",
        standardName: "Unit",
        description: "Unit of measurement",
        alternativeNames: ["units", "measurement unit", "uom", "metric", "measurement"],
        example: "cm, kg, ms"
      },
      {
        field: "notes",
        standardName: "Notes",
        description: "Additional comments or observations",
        alternativeNames: ["notes", "comments", "remarks", "observations"],
        example: "Athlete reported slight fatigue"
      },
      {
        field: "threshold_flag",
        standardName: "Threshold Flag",
        description: "Status indicator for threshold breaches",
        alternativeNames: ["flag", "status", "alert", "warning", "risk"],
        example: "green, yellow, red"
      }
    ]
  }
];

// Player-specific synonyms (not tied to GPS/performance categories)
export const PLAYER_FIELD_SYNONYMS: Record<string, string[]> = {
  first_name: ["first name", "firstname", "first", "given name"],
  last_name: ["last name", "lastname", "surname", "last", "family name"],
  name: ["name", "player name", "full name", "athlete name", "athlete", "player"],
  position: ["position", "pos", "playing position", "role"],
  jersey_number: ["jersey number", "jersey", "shirt number", "shirt", "number", "squad number", "kit number", "no", "#", "shirt #", "shirt#"],
  height_cm: ["height cm", "height", "height (cm)", "ht", "stature"],
  weight_kg: ["weight kg", "weight", "weight (kg)", "mass", "body weight", "bodyweight", "bw", "body mass"],
  date_of_birth: ["date of birth", "dob", "birth date", "birthday", "birthdate"],
  age: ["age", "years"],
  dominant_foot: ["dominant foot", "foot", "preferred foot", "strong foot", "footed", "footedness"],
  external_ref: ["external ref", "external id", "ext id", "player id", "id", "ref"],
};

// Generate COLUMN_SYNONYMS from TERMINOLOGY_DATA for use in auto-mapping
export function generateColumnSynonyms(): Record<string, string[]> {
  const synonyms: Record<string, string[]> = {};
  
  for (const category of TERMINOLOGY_DATA) {
    for (const term of category.terms) {
      synonyms[term.field] = term.alternativeNames;
    }
  }

  // Also include player field synonyms
  for (const [field, alts] of Object.entries(PLAYER_FIELD_SYNONYMS)) {
    synonyms[field] = alts;
  }
  
  return synonyms;
}
